/**
 * EnvironmentSettings - Infrastructure & Stack configuration tab
 * 
 * Settings:
 * - Core Infrastructure (cloud, region, compute)
 * - Service Mapping (database, storage, cache, queue, auth)
 * - Networking & URLs
 * - Development Workflow
 * - Secrets & Config
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Loader2, 
  Cloud,
  Server,
  Database,
  Lock,
  GitBranch,
  MapPin
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { deliveryConfigApi } from '../../services/api';

const SECTION_ICONS = {
  infrastructure: Cloud,
  services: Database,
  networking: Server,
  workflow: GitBranch,
  secrets: Lock
};

const EnvironmentSettings = ({ 
  productId,
  config, 
  options, 
  isOwner, 
  saving, 
  onUpdate,
  urlsDisabled = false,
  urlsDisabledMessage = ''
}) => {
  const [expandedSection, setExpandedSection] = useState('infrastructure');
  const [cloudOptions, setCloudOptions] = useState({
    regions: {},
    compute: {},
    ingress: {},
    database: {},
    storage: {},
    cache: {},
    queue: {},
    auth: {},
    ci_provider: {}
  });
  const [loadingOptions, setLoadingOptions] = useState(true);
  
  // Local state for text inputs (save on blur, not on every keystroke)
  const [localTextValues, setLocalTextValues] = useState({});

  // Sync local text values when config changes from outside (e.g., preset applied)
  useEffect(() => {
    // Only sync if we have config and local values are empty or config changed significantly
    if (config) {
      setLocalTextValues(prev => {
        const newValues = { ...prev };
        // Sync text fields from config
        if (config.workflow?.default_branch !== undefined && prev['workflow.default_branch'] === undefined) {
          newValues['workflow.default_branch'] = config.workflow.default_branch;
        }
        if (config.workflow?.env_var_prefix !== undefined && prev['workflow.env_var_prefix'] === undefined) {
          newValues['workflow.env_var_prefix'] = config.workflow.env_var_prefix;
        }
        // URL fields
        ['local', 'development', 'staging', 'production'].forEach(urlType => {
          const key = `url.${urlType}`;
          if (config.networking?.urls?.[urlType] !== undefined && prev[key] === undefined) {
            newValues[key] = config.networking.urls[urlType];
          }
        });
        return newValues;
      });
    }
  }, [config]);

  // Load all cloud provider options on mount
  useEffect(() => {
    const loadCloudOptions = async () => {
      try {
        const response = await deliveryConfigApi.getCloudRegions(productId);
        setCloudOptions({
          regions: response.data?.regions || {},
          compute: response.data?.compute || {},
          ingress: response.data?.ingress || {},
          database: response.data?.database || {},
          storage: response.data?.storage || {},
          cache: response.data?.cache || {},
          queue: response.data?.queue || {},
          auth: response.data?.auth || {},
          ci_provider: response.data?.ci_provider || {}
        });
      } catch (error) {
        console.error('Failed to load cloud options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };
    
    if (productId) {
      loadCloudOptions();
    }
  }, [productId]);

  if (!options) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const handleFieldChange = (section, field, value) => {
    if (!isOwner) return;
    
    // Build nested update object
    if (section === 'infrastructure') {
      // Collect all changes in a single update to avoid race conditions
      const changes = { [field]: value, _is_infrastructure_change: true };
      
      // If cloud provider changed, clear all provider-specific fields
      if (field === 'cloud_provider') {
        const clearIfIncompatible = (optionType, currentValue, isNested = false) => {
          const newOptions = cloudOptions[optionType]?.[value];
          if (!newOptions) {
            return '';
          } else if (currentValue && !newOptions.find(o => o.value === currentValue)) {
            return '';
          }
          return null; // Keep current value
        };
        
        // Infrastructure fields
        const newRegion = clearIfIncompatible('regions', config?.region);
        if (newRegion !== null) changes.region = newRegion;
        
        const newCompute = clearIfIncompatible('compute', config?.compute);
        if (newCompute !== null) changes.compute = newCompute;
        
        // Service fields (nested under services)
        const newDb = clearIfIncompatible('database', config?.services?.database);
        const newStorage = clearIfIncompatible('storage', config?.services?.storage);
        const newCache = clearIfIncompatible('cache', config?.services?.cache);
        const newQueue = clearIfIncompatible('queue', config?.services?.queue);
        const newAuth = clearIfIncompatible('auth', config?.services?.auth);
        
        if (newDb !== null || newStorage !== null || newCache !== null || newQueue !== null || newAuth !== null) {
          changes.services = {};
          if (newDb !== null) changes.services.database = newDb;
          if (newStorage !== null) changes.services.storage = newStorage;
          if (newCache !== null) changes.services.cache = newCache;
          if (newQueue !== null) changes.services.queue = newQueue;
          if (newAuth !== null) changes.services.auth = newAuth;
        }
        
        // Networking fields
        const newIngress = clearIfIncompatible('ingress', config?.networking?.ingress);
        if (newIngress !== null) {
          changes.networking = { ingress: newIngress };
        }
      }
      
      // Send all changes in a single API call
      onUpdate(changes);
    } else {
      // Nested under section
      onUpdate({ [section]: { [field]: value } });
    }
  };

  const handleUrlChange = (urlType, value) => {
    if (!isOwner) return;
    onUpdate({ 
      networking: { 
        urls: { 
          ...config?.networking?.urls,
          [urlType]: value 
        } 
      } 
    });
  };

  // Helper to get provider-specific options
  const getOptionsForProvider = (optionType) => {
    const provider = config?.cloud_provider;
    if (!provider || loadingOptions) return null;
    return cloudOptions[optionType]?.[provider];
  };

  // Get regions for the currently selected cloud provider
  const getRegionsForProvider = () => getOptionsForProvider('regions');

  // Get compute options for the currently selected cloud provider
  const getComputeForProvider = () => getOptionsForProvider('compute');

  // Get ingress options for the currently selected cloud provider
  const getIngressForProvider = () => getOptionsForProvider('ingress');

  // Dynamic field types that depend on cloud provider
  const PROVIDER_DEPENDENT_FIELDS = {
    'database': { optionKey: 'database', label: 'Primary Database', icon: Database },
    'storage': { optionKey: 'storage', label: 'File/Blob Storage', icon: Server },
    'cache': { optionKey: 'cache', label: 'Caching Layer', icon: Server },
    'queue': { optionKey: 'queue', label: 'Async Messaging', icon: Server },
    'auth': { optionKey: 'auth', label: 'Identity Provider', icon: Lock, dependsOn: 'cloud_provider' },
    'ingress': { optionKey: 'ingress', label: 'Ingress Pattern', icon: Server, dependsOn: 'cloud_provider' },
    'ci_provider': { optionKey: 'ci_provider', label: 'CI/CD Provider', icon: GitBranch, dependsOn: 'repo_host' },
  };

  // Get options for a field that depends on another field
  const getDependentOptions = (optionKey, dependsOnField) => {
    const dependsOnValue = config?.[dependsOnField] || config?.workflow?.[dependsOnField];
    if (!dependsOnValue || loadingOptions) return null;
    return cloudOptions[optionKey]?.[dependsOnValue];
  };

  const renderField = (section, fieldKey, fieldConfig) => {
    const value = section === 'infrastructure' 
      ? config?.[fieldKey]
      : config?.[section]?.[fieldKey];

    // Check if this field is dependent on another field
    const providerDep = PROVIDER_DEPENDENT_FIELDS[fieldKey];
    if (providerDep) {
      const dependsOn = providerDep.dependsOn || 'cloud_provider'; // Default to cloud_provider
      const dependsOnValue = dependsOn === 'cloud_provider' 
        ? config?.cloud_provider 
        : (config?.[dependsOn] || config?.workflow?.[dependsOn]);
      
      const fieldOptions = dependsOn === 'cloud_provider'
        ? getOptionsForProvider(providerDep.optionKey)
        : getDependentOptions(providerDep.optionKey, dependsOn);
      
      const IconComponent = providerDep.icon;
      
      // If dependency not selected
      if (!dependsOnValue) {
        const dependsOnLabel = dependsOn === 'cloud_provider' ? 'cloud provider' : dependsOn.replace(/_/g, ' ');
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            {fieldConfig.description && (
              <p className="text-xs text-gray-500">{fieldConfig.description}</p>
            )}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <IconComponent className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Select a {dependsOnLabel} first</span>
            </div>
          </div>
        );
      }
      
      // Show dependent dropdown
      if (fieldOptions && fieldOptions.length > 0) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            {fieldConfig.description && (
              <p className="text-xs text-gray-500 -mt-1">{fieldConfig.description}</p>
            )}
            <Select
              value={value || ''}
              onValueChange={(v) => handleFieldChange(section, fieldKey, v)}
              disabled={!isOwner || saving || loadingOptions}
            >
              <SelectTrigger id={`${section}-${fieldKey}`} data-testid={`env-${section}-${fieldKey}`}>
                <SelectValue placeholder={loadingOptions ? "Loading..." : `Select ${fieldConfig.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="py-2">
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      {opt.description && (
                        <span className="text-xs text-gray-500">{opt.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }
      
      return null;
    }

    // Special handling for ingress field (legacy - now handled above)
    if (fieldKey === 'ingress') {
      const ingressOptions = getIngressForProvider();
      const provider = config?.cloud_provider;
      
      // If no provider selected
      if (!provider) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Server className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Select a cloud provider first</span>
            </div>
          </div>
        );
      }
      
      // Show provider-specific ingress dropdown
      if (ingressOptions && ingressOptions.length > 0) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <Select
              value={value || ''}
              onValueChange={(v) => handleFieldChange(section, fieldKey, v)}
              disabled={!isOwner || saving || loadingOptions}
            >
              <SelectTrigger id={`${section}-${fieldKey}`} data-testid={`env-${section}-${fieldKey}`}>
                <SelectValue placeholder={loadingOptions ? "Loading..." : "Select ingress pattern"} />
              </SelectTrigger>
              <SelectContent>
                {ingressOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }
      
      return null;
    }

    // Special handling for compute field
    if (fieldKey === 'compute') {
      const computeOptions = getComputeForProvider();
      const provider = config?.cloud_provider;
      
      // If no provider selected
      if (!provider) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Server className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Select a cloud provider first</span>
            </div>
          </div>
        );
      }
      
      // Show provider-specific compute dropdown
      if (computeOptions && computeOptions.length > 0) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <Select
              value={value || ''}
              onValueChange={(v) => handleFieldChange(section, fieldKey, v)}
              disabled={!isOwner || saving || loadingOptions}
            >
              <SelectTrigger id={`${section}-${fieldKey}`} data-testid={`env-${section}-${fieldKey}`}>
                <SelectValue placeholder={loadingOptions ? "Loading..." : "Select compute option"} />
              </SelectTrigger>
              <SelectContent>
                {computeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }
      
      // Fallback to default options if no provider-specific options
      return null;
    }

    // Special handling for region field
    if (fieldKey === 'region') {
      const regions = getRegionsForProvider();
      const provider = config?.cloud_provider;
      
      // If no provider selected
      if (!provider) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Select a cloud provider first</span>
            </div>
          </div>
        );
      }
      
      // If provider doesn't require region selection
      if (regions === null) {
        return (
          <div key={fieldKey} className="space-y-2">
            <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Not required for {provider.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </div>
          </div>
        );
      }
      
      // Show region dropdown
      return (
        <div key={fieldKey} className="space-y-2">
          <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
          <Select
            value={value || ''}
            onValueChange={(v) => handleFieldChange(section, fieldKey, v)}
            disabled={!isOwner || saving || loadingOptions}
          >
            <SelectTrigger id={`${section}-${fieldKey}`} data-testid={`env-${section}-${fieldKey}`}>
              <SelectValue placeholder={loadingOptions ? "Loading regions..." : "Select a region"} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {regions && regions.map((region) => (
                <SelectItem key={region.value} value={region.value}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">{region.value}</span>
                    <span>{region.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (fieldConfig.type === 'select') {
      const selectOptions = Array.isArray(fieldConfig.options) 
        ? fieldConfig.options 
        : Object.keys(fieldConfig.options || {});

      // Convert value to string for Select component, but preserve 0 as valid value
      const selectValue = value !== undefined && value !== null ? String(value) : '';

      return (
        <div key={fieldKey} className="space-y-2">
          <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
          {fieldConfig.description && (
            <p className="text-xs text-gray-500 -mt-1">{fieldConfig.description}</p>
          )}
          <Select
            value={selectValue}
            onValueChange={(v) => {
              // Convert back to number if the original options had numeric values
              const originalOpt = selectOptions.find(opt => 
                String(typeof opt === 'object' && opt !== null ? opt.value : opt) === v
              );
              const finalValue = originalOpt && typeof (originalOpt.value ?? originalOpt) === 'number' 
                ? Number(v) 
                : v;
              handleFieldChange(section, fieldKey, finalValue);
            }}
            disabled={!isOwner || saving}
          >
            <SelectTrigger id={`${section}-${fieldKey}`} data-testid={`env-${section}-${fieldKey}`}>
              <SelectValue placeholder={`Select ${fieldConfig.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.map((opt) => {
                // Always convert to string for SelectItem value (Radix requirement)
                const optValue = String(typeof opt === 'object' && opt !== null ? opt.value : opt);
                const optLabel = typeof opt === 'object' && opt !== null 
                  ? opt.label 
                  : String(opt).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                const optDescription = typeof opt === 'object' && opt !== null ? opt.description : null;
                return (
                  <SelectItem key={optValue} value={optValue} className={optDescription ? "py-2" : ""}>
                    <div className="flex flex-col">
                      <span>{optLabel}</span>
                      {optDescription && (
                        <span className="text-xs text-gray-500">{optDescription}</span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (fieldConfig.type === 'text') {
      const localKey = `${section}.${fieldKey}`;
      const localValue = localTextValues[localKey] !== undefined ? localTextValues[localKey] : (value || '');
      
      return (
        <div key={fieldKey} className="space-y-2">
          <Label htmlFor={`${section}-${fieldKey}`}>{fieldConfig.label}</Label>
          <Input
            id={`${section}-${fieldKey}`}
            value={localValue}
            onChange={(e) => setLocalTextValues(prev => ({ ...prev, [localKey]: e.target.value }))}
            onBlur={(e) => {
              // Only save if value actually changed
              if (e.target.value !== (value || '')) {
                handleFieldChange(section, fieldKey, e.target.value);
              }
            }}
            placeholder={fieldConfig.placeholder}
            disabled={!isOwner || saving}
            data-testid={`env-${section}-${fieldKey}`}
          />
        </div>
      );
    }

    if (fieldConfig.type === 'url_group') {
      // Check if URLs should be disabled (bootstrap mode)
      const isUrlFieldDisabled = urlsDisabled || !isOwner || saving;
      
      return (
        <div key={fieldKey} className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{fieldConfig.label}</Label>
            {urlsDisabled && urlsDisabledMessage && (
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 text-xs">
                {urlsDisabledMessage}
              </Badge>
            )}
          </div>
          <div className={`grid grid-cols-2 gap-3 ${urlsDisabled ? 'opacity-60' : ''}`}>
            {fieldConfig.fields.map((urlType) => {
              const localKey = `url.${urlType}`;
              const configValue = config?.networking?.urls?.[urlType] || '';
              const localValue = localTextValues[localKey] !== undefined ? localTextValues[localKey] : configValue;
              
              return (
                <div key={urlType} className="space-y-1">
                  <Label className="text-xs text-gray-500 capitalize">{urlType}</Label>
                  <Input
                    value={localValue}
                    onChange={(e) => setLocalTextValues(prev => ({ ...prev, [localKey]: e.target.value }))}
                    onBlur={(e) => {
                      // Only save if value actually changed
                      if (e.target.value !== configValue) {
                        handleUrlChange(urlType, e.target.value);
                      }
                    }}
                    placeholder={urlsDisabled ? 'Auto-populated on bootstrap' : `https://${urlType === 'local' ? 'localhost:3000' : `${urlType}.example.com`}`}
                    disabled={isUrlFieldDisabled}
                    className="text-sm"
                    data-testid={`env-url-${urlType}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6" data-testid="environment-settings">
      {/* Settings Sections */}
      {Object.entries(options).map(([sectionKey, section]) => {
        const Icon = SECTION_ICONS[sectionKey] || Server;
        const isExpanded = expandedSection === sectionKey;
        
        return (
          <Card key={sectionKey}>
            <CardHeader 
              className="pb-3 cursor-pointer"
              onClick={() => setExpandedSection(isExpanded ? null : sectionKey)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <CardTitle className="text-base">{section.label}</CardTitle>
                </div>
                <span className="text-gray-400 text-sm">
                  {isExpanded ? '−' : '+'}
                </span>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent className="relative">
                {saving && (
                  <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                )}
                <div className="grid gap-4">
                  {Object.entries(section.fields).map(([fieldKey, fieldConfig]) => 
                    renderField(sectionKey === 'infrastructure' ? 'infrastructure' : sectionKey, fieldKey, fieldConfig)
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default EnvironmentSettings;
